import Link from 'next/link';

import { cn } from '@utils';
import { resolveStoryblokLinkHref } from '@utils/resolveStoryblokLinkHref';

import { SiteLogo } from './SiteLogo';
import type { NavigationItemData, SiteSettingsData } from './types';

export type MobileOpenState = Record<string, boolean>;

export const headerHeightClass = 'h-20 sm:h-[5.5rem] lg:h-24';
export const headerScrollThreshold = 32;

const logoTransitionClass =
  'transition-[width,transform,color,filter] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]';

export function isPathActive(pathname: string, href: string) {
  if (href === '/') {
    return pathname === '/';
  }

  return pathname === href || pathname.startsWith(`${href}/`);
}

export function isNavigationItemActive(item: NavigationItemData, pathname: string) {
  const href = resolveStoryblokLinkHref(item.href);

  return (
    isPathActive(pathname, href) ||
    item.children?.some((child) => isPathActive(pathname, resolveStoryblokLinkHref(child.href))) ===
      true
  );
}

export function getOpenMobileGroups(
  navigation: SiteSettingsData['navigation'],
  pathname: string,
): MobileOpenState {
  return navigation.reduce<MobileOpenState>((groups, item) => {
    const hasActiveChild =
      item.children?.some((child) =>
        isPathActive(pathname, resolveStoryblokLinkHref(child.href)),
      ) ?? false;

    if (hasActiveChild) {
      groups[item.label] = true;
    }

    return groups;
  }, {});
}

export function Chevron({ open = false }: { open?: boolean }) {
  return (
    <svg
      aria-hidden="true"
      className={cn('h-4 w-4 transition-transform duration-200', open && 'rotate-180')}
      fill="none"
      viewBox="0 0 24 24"
    >
      <path
        d="M6 9L12 15L18 9"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

type SiteHeaderBrandLinkProps = {
  headerIsSolid: boolean;
  viewport: 'desktop' | 'mobile';
};

export function SiteHeaderBrandLink({ headerIsSolid, viewport }: SiteHeaderBrandLinkProps) {
  const isMobile = viewport === 'mobile';

  return (
    <Link
      aria-label="Met Liefde Videografie"
      className={cn(
        'relative z-10 inline-flex items-center transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
        isMobile ? 'max-w-[calc(100vw-6rem)] min-w-0 shrink' : 'justify-center',
        headerIsSolid ? 'translate-y-0' : isMobile ? 'translate-y-2' : 'translate-y-3',
      )}
      href="/"
    >
      <span
        className={cn(
          'inline-flex max-w-full',
          logoTransitionClass,
          isMobile
            ? headerIsSolid
              ? 'w-[8rem] text-primary sm:w-[8.75rem]'
              : 'w-[9.5rem] scale-[1.02] text-white drop-shadow-[0_6px_18px_rgba(79,73,68,0.24)] sm:w-[10.5rem]'
            : headerIsSolid
              ? 'w-[7.75rem] text-primary xl:w-[8.5rem]'
              : 'w-[9.25rem] scale-[1.03] text-white drop-shadow-[0_6px_18px_rgba(79,73,68,0.24)] xl:w-[10rem]',
        )}
      >
        <SiteLogo />
      </span>
    </Link>
  );
}
